import { REGISTERED_PATTERN_IDS } from "@/arc/arc-runner";


// ── Module Registration ──

export type ModuleDomain =
  | "source_config"
  | "lead_validation"
  | "pipeline_execution"
  | "automation"
  | "timeline"
  | "follow_up"
  | "events"
  | "scoring";

export interface ModuleRegistration {
  domain: ModuleDomain;
  ownerFile: string;
  description: string;
  writesTo: string[];
  readsFrom: string[];
}

export const MODULE_REGISTRY: ModuleRegistration[] = [
  {
    domain: "source_config",
    ownerFile: "pipelineIntelligence/context.tsx",
    description: "Lead source profiles — attribution, funnel, urgency, AI defaults",
    writesTo: ["SourceProfile", "AISourceConfig"],
    readsFrom: [],
  },
  {
    domain: "lead_validation",
    ownerFile: "leadIntegrityEngine.ts",
    description: "Intake validation — completeness, spam, duplicates, trust scoring",
    writesTo: ["IntakeRecord", "ValidationResult", "DuplicateMatch"],
    readsFrom: ["SourceProfile"],
  },
  {
    domain: "pipeline_execution",
    ownerFile: "pipelineIntelligence/context.tsx",
    description: "Response flows, AI conversations, sequences, adaptive optimization",
    writesTo: ["ResponseFlow", "AIConversationDraft", "ActiveSequence"],
    readsFrom: ["SourceProfile", "Opportunity"],
  },
  {
    domain: "automation",
    ownerFile: "automationEngine.ts",
    description: "Rule definitions, condition evaluation, action execution",
    writesTo: ["AutomationRule", "AutomationExecution"],
    readsFrom: ["SystemEvent", "Job"],
  },
  {
    domain: "timeline",
    ownerFile: "communicationEngine.ts",
    description: "Unified communication timeline, system event types, job lifecycle templates",
    writesTo: ["CommunicationEvent"],
    readsFrom: ["Job", "Issue", "InstallerWrapRecord", "Message"],
  },
  {
    domain: "follow_up",
    ownerFile: "unifiedFollowUp.ts",
    description: "All follow-up logic across contexts: sales, install, post-job",
    writesTo: ["FollowUpTask", "InstallFollowUpTask"],
    readsFrom: ["Opportunity", "InstallerWrapRecord", "Job"],
  },
  {
    domain: "events",
    ownerFile: "eventFactory.ts",
    description: "Canonical event creation — IDs, timestamps, validation",
    writesTo: ["SystemEvent"],
    readsFrom: [],
  },
  {
    domain: "scoring",
    ownerFile: "scoring/index.ts (conceptual)",
    description: "Separated: IntegrityScore (leadIntegrityEngine) vs OpportunityScore (adaptiveEngine)",
    writesTo: ["IntegrityScore", "OpportunityScore", "LeadScore"],
    readsFrom: ["IntakeRecord", "Opportunity", "SourcePerformanceMetrics"],
  },
];

// ── Scoring Boundary ──

export interface IntegrityScoreDef {
  domain: "validation";
  factors: ["data_completeness", "phone_validity", "email_validity", "duplicate_risk", "geo_match", "project_fit"];
  output: "trust_level";
  range: [0, 100];
}

export interface OpportunityScoreDef {
  domain: "execution";
  factors: ["source_quality", "response_speed", "engagement", "historical_conversion", "job_value", "recency"];
  output: "priority_level";
  range: [0, 100];
}

// ── Template Ownership ──

/**
 * Template boundaries:
 * - communicationEngine.ts MESSAGE_TEMPLATES → job lifecycle (deposit, install, completion)
 * - pipelineIntelligence SEED_TEMPLATES → lead response (initial, follow-up, objection)
 * - FollowUpEngineContext SEED_TEMPLATES → sales disposition (thank-you, follow-up sequences)
 */

// ── Validation Helper ──

export function validateModuleOwnership(
  domain: ModuleDomain,
  callerFile: string,
): { valid: boolean; owner: string } {
  const reg = MODULE_REGISTRY.find(m => m.domain === domain);
  if (!reg) return { valid: false, owner: "unknown" };
  const valid = reg.ownerFile === callerFile || callerFile.includes(reg.ownerFile.split("/")[0]);
  return { valid, owner: reg.ownerFile };
}

// ── Runtime Enforcement (HARD MODE) ──

/** Violation log for architecture health checks */
const _violations: Array<{ timestamp: string; caller: string; domain: string; operation: string }> = [];

export function getArchitectureViolations() {
  return [..._violations];
}

export function clearArchitectureViolations() {
  _violations.length = 0;
}

/**
 * HARD enforce module ownership at runtime.
 * DEV: throws Error immediately — no silent failures.
 * PROD: logs warning + records violation for health monitoring.
 */
export function enforceOwnership(
  domain: ModuleDomain,
  callerFile: string,
  operation: string,
): void {
  const result = validateModuleOwnership(domain, callerFile);
  if (!result.valid) {
    const message = `[MODULE VIOLATION] "${callerFile}" attempted to write to "${domain}" domain (owned by ${result.owner}). Operation: ${operation}`;
    _violations.push({ timestamp: new Date().toISOString(), caller: callerFile, domain, operation });

    if (import.meta.env.DEV) {
      throw new Error(message);
    } else {
      console.warn(message);
    }
  }
}

// ── Execution Engine Registration ──

export const EXECUTION_ENGINE_OWNER = "executionEngine.ts";

/**
 * Authorized callers that may invoke automation/execution logic.
 * Everything else MUST route through executionEngine.ts.
 */
const AUTHORIZED_EXECUTION_CALLERS = [
  "executionEngine.ts",
  "automationRunner.ts",        // delegated by execution engine
  "AutomationRunnerContext.tsx", // React wrapper for execution engine
];

/**
 * Validates that execution flows through the unified ExecutionEngine.
 * Any module attempting to independently orchestrate triggers/sequences
 * should route through executionEngine.ts instead.
 */
export function validateExecutionPath(callerFile: string): boolean {
  return AUTHORIZED_EXECUTION_CALLERS.some(f => callerFile.includes(f));
}

/**
 * HARD enforcement of execution path.
 * DEV: throws Error if unauthorized caller attempts execution.
 * PROD: logs violation + allows execution with warning.
 */
export function assertExecutionPath(callerFile: string): void {
  if (!validateExecutionPath(callerFile)) {
    const message = `[EXECUTION VIOLATION] "${callerFile}" attempted direct execution. All execution must flow through ExecutionEngine (${EXECUTION_ENGINE_OWNER}).`;
    _violations.push({ timestamp: new Date().toISOString(), caller: callerFile, domain: "execution", operation: "direct_execution" });

    if (import.meta.env.DEV) {
      throw new Error(message);
    } else {
      console.warn(message);
    }
  }
}

// ── Internal Module Guards ──

/**
 * INTERNAL_ONLY modules that must not be imported directly by application code.
 * Only their designated facade (unifiedFollowUp.ts, executionEngine.ts) may use them.
 */
export const INTERNAL_MODULES: Record<string, { facade: string; allowedCallers: string[] }> = {
  "followUpEngine.ts": {
    facade: "unifiedFollowUp.ts",
    allowedCallers: ["unifiedFollowUp.ts", "FollowUpEngineContext.tsx"],
  },
  "installFollowUpEngine.ts": {
    facade: "unifiedFollowUp.ts",
    allowedCallers: ["unifiedFollowUp.ts"],
  },
  "automationEngine.ts": {
    facade: "executionEngine.ts",
    allowedCallers: ["executionEngine.ts", "automationRunner.ts", "AutomationRunnerContext.tsx", "AutomationControlPanel.tsx"],
  },
  "automationRunner.ts": {
    facade: "executionEngine.ts",
    allowedCallers: ["executionEngine.ts", "AutomationRunnerContext.tsx"],
  },
};

/**
 * Validate that a caller is authorized to import an internal module.
 * DEV: throws Error for unauthorized access.
 * PROD: logs + auto-routes to facade.
 */
export function assertInternalAccess(internalModule: string, callerFile: string): void {
  const config = INTERNAL_MODULES[internalModule];
  if (!config) return; // Not a guarded module

  const isAllowed = config.allowedCallers.some(c => callerFile.includes(c));
  if (!isAllowed) {
    const message = `[INTERNAL MODULE VIOLATION] "${callerFile}" directly imported "${internalModule}". Use "${config.facade}" instead.`;
    _violations.push({ timestamp: new Date().toISOString(), caller: callerFile, domain: "internal_access", operation: `import:${internalModule}` });

    if (import.meta.env.DEV) {
      throw new Error(message);
    } else {
      console.warn(`${message} Auto-routing to ${config.facade}.`);
    }
  }
}

// ── Duplicate Path Detection ──

const _activeResponsibilities = new Map<string, string>();

/**
 * Register an active responsibility claim.
 * If another module already claimed the same responsibility, log a violation.
 */
export function claimResponsibility(responsibility: string, claimant: string): void {
  const existing = _activeResponsibilities.get(responsibility);
  if (existing && existing !== claimant) {
    const message = `[DUPLICATE PATH] "${responsibility}" claimed by both "${existing}" and "${claimant}". Only one module should own this.`;
    _violations.push({ timestamp: new Date().toISOString(), caller: claimant, domain: "duplicate_path", operation: responsibility });

    if (import.meta.env.DEV) {
      throw new Error(message);
    } else {
      console.warn(message);
    }
  }
  _activeResponsibilities.set(responsibility, claimant);
}

export function clearResponsibilityClaims(): void {
  _activeResponsibilities.clear();
}

// ── ARC Integration ──

/**
 * The full set of pattern IDs required by the ARC engine.
 * Health check verifies all are registered in arc-runner.ts.
 */
const REQUIRED_ARC_PATTERN_IDS = [
  "blocked_jobs",
  "revenue_at_risk",
  "permit_delays",
  "qa_failures",
  "collections_overdue",
];

// ── Architecture Health Check ──

export interface ArchitectureHealthReport {
  healthy: boolean;
  violations: typeof _violations;
  checks: Array<{ name: string; passed: boolean; detail: string }>;
}

/**
 * Run a comprehensive architecture health check.
 * Validates all structural invariants.
 */
export function runArchitectureHealthCheck(): ArchitectureHealthReport {
  const checks: ArchitectureHealthReport["checks"] = [];

  // Check 1: No duplicate domain registrations
  const domains = MODULE_REGISTRY.map(m => m.domain);
  const uniqueDomains = new Set(domains);
  checks.push({
    name: "unique_domains",
    passed: uniqueDomains.size === domains.length,
    detail: `${uniqueDomains.size}/${domains.length} unique domains`,
  });

  // Check 2: No writesTo overlap
  const writeMap = new Map<string, string>();
  let writeOverlaps = 0;
  MODULE_REGISTRY.forEach(m => {
    m.writesTo.forEach(w => {
      if (writeMap.has(w) && writeMap.get(w) !== m.ownerFile) writeOverlaps++;
      writeMap.set(w, m.ownerFile);
    });
  });
  checks.push({
    name: "no_write_overlaps",
    passed: writeOverlaps === 0,
    detail: `${writeOverlaps} write overlaps found`,
  });

  // Check 3: Execution engine is registered
  checks.push({
    name: "execution_engine_registered",
    passed: EXECUTION_ENGINE_OWNER === "executionEngine.ts",
    detail: `Owner: ${EXECUTION_ENGINE_OWNER}`,
  });

  // Check 4: All internal modules have facades defined
  const allInternal = Object.keys(INTERNAL_MODULES);
  checks.push({
    name: "internal_modules_guarded",
    passed: allInternal.length >= 4,
    detail: `${allInternal.length} internal modules guarded`,
  });

  // Check 5: No unresolved violations
  checks.push({
    name: "no_runtime_violations",
    passed: _violations.length === 0,
    detail: `${_violations.length} violations recorded`,
  });

  // Check 6: All required ARC patterns are registered
  const missingPatterns = REQUIRED_ARC_PATTERN_IDS.filter(id => !REGISTERED_PATTERN_IDS.includes(id));
  checks.push({
    name: "arc_patterns_complete",
    passed: missingPatterns.length === 0,
    detail: missingPatterns.length === 0
      ? `All ${REQUIRED_ARC_PATTERN_IDS.length} patterns registered`
      : `Missing: ${missingPatterns.join(", ")}`,
  });

  // Check 7: All MODULE_REGISTRY entries declare at least one writesTo
  const emptyWriteModules = MODULE_REGISTRY.filter(m => m.writesTo.length === 0).map(m => m.domain);
  checks.push({
    name: "registry_writes_declared",
    passed: emptyWriteModules.length === 0,
    detail: emptyWriteModules.length === 0
      ? `All ${MODULE_REGISTRY.length} modules declare writes`
      : `No writesTo: ${emptyWriteModules.join(", ")}`,
  });

  // Check 8: All INTERNAL_MODULES have a non-empty facade string
  const invalidFacades = Object.entries(INTERNAL_MODULES)
    .filter(([, config]) => !config.facade || config.facade.trim() === "")
    .map(([mod]) => mod);
  checks.push({
    name: "internal_facades_valid",
    passed: invalidFacades.length === 0,
    detail: invalidFacades.length === 0
      ? `All ${Object.keys(INTERNAL_MODULES).length} facades valid`
      : `Missing facade: ${invalidFacades.join(", ")}`,
  });

  const healthy = checks.every(c => c.passed);

  return {
    healthy,
    violations: getArchitectureViolations(),
    checks,
  };
}
